import { z } from "zod/v4";

// ── Channel Types (platform-agnostic) ─────────────────────────────────────────

export const NotificationChannelTypeList = ["slack"] as const;

export type NotificationChannelType =
  (typeof NotificationChannelTypeList)[number];

// ── Notification Types ────────────────────────────────────────────────────────
export const AlertNotificationTypeList = [
  "NO_ORDERS_ALERT",
  "NO_VISITORS_ALERT",
  "BIG_WIN_DETECTED",
] as const;

export const MilestoneNotificationTypeList = [
  "MILESTONE_SAMPLE_SIZE",
  "MILESTONE_STATISTICAL_SIGNIFICANCE",
  "TEST_RUNTIME_MILESTONE",
] as const;

export const TestLifecycleNotificationTypeList = [
  "TEST_STARTED",
  "TEST_PAUSED_RESUMED",
  "TEST_COMPLETED",
] as const;

export const SummaryNotificationTypeList = [
  "WEEKLY_SUMMARY",
  "DAILY_SUMMARY",
] as const;

export const AllNotificationTypeList = [
  ...AlertNotificationTypeList,
  ...MilestoneNotificationTypeList,
  ...TestLifecycleNotificationTypeList,
  ...SummaryNotificationTypeList,
] as const;

export type NotificationType = (typeof AllNotificationTypeList)[number];

export const ROUTE_CATEGORIES: Record<string, NotificationType[]> = {
  ALERTS: [...AlertNotificationTypeList],
  MILESTONES: [...MilestoneNotificationTypeList],
  TEST_LIFECYCLE: [...TestLifecycleNotificationTypeList],
  SUMMARIES: [...SummaryNotificationTypeList],
} as const;

export type RouteCategory = keyof typeof ROUTE_CATEGORIES;

// ── Notification Log Status ───────────────────────────────────────────────────

export const NotificationLogStatusList = [
  "sent",
  "failed",
  "deferred",
  "suppressed",
] as const;

export type NotificationLogStatus = (typeof NotificationLogStatusList)[number];

// ── Notification Data Interfaces ──────────────────────────────────────────────

export interface NotificationData {
  [key: string]: unknown;
  orgId: string;
  orgName: string;
  whatsNext?: string;
}

export interface TestLifecycleData extends NotificationData {
  type: "testLifecycle";
  experienceId: string;
  experienceName: string;
  action: "start" | "pause" | "resume" | "end";
}

export interface TestCompletedData extends NotificationData {
  type: "testCompleted";
  experienceId: string;
  experienceName: string;
  winningVariantName: string | null;
  liftPercent: number | null;
  isStatSig: boolean | null;
}

export interface MilestoneData extends NotificationData {
  experienceId: string;
  experienceName: string;
  thresholdUsed: string;
}

export interface MilestoneSampleSizeData extends MilestoneData {
  type: "milestoneSampleSize";
  ordersPerVariant: number;
}

export interface MilestoneStatSigData extends MilestoneData {
  type: "milestoneStatSig";
  probabilityPercent: number;
  leadingVariantName: string;
}

export interface TestRuntimeMilestoneData extends MilestoneData {
  type: "testRuntimeMilestone";
  dayCount: number;
}

export interface AlertData extends NotificationData {
  experienceId: string;
  experienceName: string;
  whatsNext: string;
  thresholdUsed: string;
}

export interface NoOrdersAlertData extends AlertData {
  type: "noOrdersAlert";
  daysSinceLastOrder: number;
  affectedVariants: string[];
}

export interface NoVisitorsAlertData extends AlertData {
  type: "noVisitorsAlert";
  daysSinceLastVisitor: number;
  affectedVariants: string[];
}

export interface BigWinDetectedData extends AlertData {
  type: "bigWinDetected";
  winningVariantName: string;
  liftPercent: number;
  probabilityPercent: number;
  daysLive: number;
  controlOrders: number;
  winningOrders: number;
}

export interface ExperienceSummaryItem {
  type: "experienceSummary";
  experienceId: string;
  experienceName: string;
  liftPercent?: number | null;
  winningVariantName?: string | null;
  isStatSig?: boolean;
}

export interface DailySummaryData extends NotificationData {
  type: "dailySummary";
  activeExperimentCount: number;
  ordersLast24h: number;
  visitorsLast24h: number;
  reachedStatSig: ExperienceSummaryItem[];
  noActivity: ExperienceSummaryItem[];
}

export interface WeeklySummaryData extends NotificationData {
  type: "weeklySummary";
  experimentsStarted: number;
  experimentsCompleted: number;
  experimentsPaused: number;
  totalOrders: number;
  totalVisitors: number;
  statSigExperiments: ExperienceSummaryItem[];
  topPerformer: ExperienceSummaryItem | null;
}

export type SlackNotificationData =
  | TestLifecycleData
  | TestCompletedData
  | MilestoneSampleSizeData
  | MilestoneStatSigData
  | TestRuntimeMilestoneData
  | NoOrdersAlertData
  | NoVisitorsAlertData
  | BigWinDetectedData
  | ExperienceSummaryItem
  | DailySummaryData
  | WeeklySummaryData;

// ── Notification Type → Data Mapping ───────────────────────────────────────── |

export interface NotificationDataMap {
  TEST_STARTED: TestLifecycleData;
  TEST_PAUSED_RESUMED: TestLifecycleData;
  TEST_COMPLETED: TestCompletedData;
  MILESTONE_SAMPLE_SIZE: MilestoneSampleSizeData;
  MILESTONE_STATISTICAL_SIGNIFICANCE: MilestoneStatSigData;
  TEST_RUNTIME_MILESTONE: TestRuntimeMilestoneData;
  WEEKLY_SUMMARY: WeeklySummaryData;
  DAILY_SUMMARY: DailySummaryData;
  NO_ORDERS_ALERT: NoOrdersAlertData;
  NO_VISITORS_ALERT: NoVisitorsAlertData;
  BIG_WIN_DETECTED: BigWinDetectedData;
}

// ── Threshold Config Types ────────────────────────────────────────────────────

export interface MilestoneSampleSizeThreshold {
  ordersPerVariant: number;
  minimumDays: number;
}

export interface MilestoneStatSigThreshold {
  probabilityPercent: number;
  minimumDays: number;
  minimumOrdersPerVariant: number;
}

export interface TestRuntimeMilestoneThreshold {
  dayMilestones: number[];
}

export interface NoOrdersAlertThreshold {
  daysWithoutOrders: number;
}

export interface NoVisitorsAlertThreshold {
  daysWithoutVisitors: number;
}

export interface BigWinDetectedThreshold {
  minimumDays: number;
  minimumLiftPercent: number;
}

export type NotificationThresholdMap = {
  MILESTONE_SAMPLE_SIZE: MilestoneSampleSizeThreshold;
  MILESTONE_STATISTICAL_SIGNIFICANCE: MilestoneStatSigThreshold;
  TEST_RUNTIME_MILESTONE: TestRuntimeMilestoneThreshold;
  NO_ORDERS_ALERT: NoOrdersAlertThreshold;
  NO_VISITORS_ALERT: NoVisitorsAlertThreshold;
  BIG_WIN_DETECTED: BigWinDetectedThreshold;
};

export const NotificationThresholdDefaults: NotificationThresholdMap = {
  MILESTONE_SAMPLE_SIZE: { ordersPerVariant: 300, minimumDays: 7 },
  MILESTONE_STATISTICAL_SIGNIFICANCE: {
    probabilityPercent: 95,
    minimumDays: 7,
    minimumOrdersPerVariant: 300,
  },
  TEST_RUNTIME_MILESTONE: { dayMilestones: [7, 14] },
  NO_ORDERS_ALERT: { daysWithoutOrders: 1 },
  NO_VISITORS_ALERT: { daysWithoutVisitors: 1 },
  BIG_WIN_DETECTED: { minimumDays: 7, minimumLiftPercent: 10 },
};

// ── Threshold-Enabled Notification Types ─────────────────────────────────

export const ThresholdEnabledNotificationTypeList: ThresholdEnabledNotificationType[] =
  [
    "MILESTONE_SAMPLE_SIZE",
    "MILESTONE_STATISTICAL_SIGNIFICANCE",
    "TEST_RUNTIME_MILESTONE",
    "NO_ORDERS_ALERT",
    "NO_VISITORS_ALERT",
    "BIG_WIN_DETECTED",
  ] as const;

export type ThresholdEnabledNotificationType = keyof NotificationThresholdMap;

// ── Threshold Config DTO ─────────────────────────────────────────────────

export const NotificationThresholdConfigDtoSchema = z.object({
  id: z.uuid(),
  integrationId: z.uuid(),
  orgId: z.uuid().nullable(),
  notificationType: z.string(),
  thresholds: z.record(z.string(), z.unknown()),
});

export type NotificationThresholdConfigDto = z.infer<
  typeof NotificationThresholdConfigDtoSchema
>;

// ── SQS Event Types ───────────────────────────────────────────────────────────

export const NotificationEventSchema = z.object({
  orgId: z.uuid(),
  notificationType: z.enum(AllNotificationTypeList),
  experienceId: z.uuid().optional(),
  data: z.record(z.string(), z.unknown()),
  timestamp: z.number(),
  idempotencyKey: z.string(),
});

export type NotificationEvent = z.infer<typeof NotificationEventSchema>;

export const SlackNotificationSqsEventSchema = NotificationEventSchema.extend({
  channelType: z.literal("slack"),
  channelBindingId: z.uuid().optional(),
});

export type SlackNotificationSqsEvent = z.infer<
  typeof SlackNotificationSqsEventSchema
>;

// ── Detection Fan-Out SQS Event ──────────────────────────────────────────────

export const DetectionCronTypeList = [
  "milestone",
  "alert",
  "daily_summary",
  "weekly_summary",
  "test_completed",
] as const;

export type DetectionCronType = (typeof DetectionCronTypeList)[number];

export const ThresholdConfigEntrySchema = z.object({
  integrationId: z.uuid(),
  orgId: z.uuid().nullable(),
  notificationType: z.string(),
  thresholds: z.unknown(),
});

export type ThresholdConfigEntry = z.infer<typeof ThresholdConfigEntrySchema>;

export const NotificationDetectionSqsEventSchema = z.object({
  cronType: z.enum(DetectionCronTypeList),
  orgId: z.uuid(),
  integrationId: z.uuid(),
  thresholdConfigs: z.array(ThresholdConfigEntrySchema),
  // Set only by test_completed, which targets the one experience that just ended.
  experienceId: z.uuid().optional(),
  // Fixed in the message, so redeliveries share an idempotency key and a later end does not.
  endedAtTs: z.number().optional(),
});

export type NotificationDetectionSqsEvent = z.infer<
  typeof NotificationDetectionSqsEventSchema
>;
